import { Helmet } from 'react-helmet-async'
import { useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useLang } from '@shared/i18n/useLang'
import { LANGUAGES, DEFAULT_LANG } from '@shared/i18n/languages'
import { SITE_URL } from '@shared/config/env'

const OG_LOCALE: Record<string, string> = { ru: 'ru_RU', zh: 'zh_CN' }

/** Strips the leading `/<lang>` segment, e.g. "/ru/kontakty" -> "/kontakty". */
function pathWithoutLang(pathname: string): string {
  const rest = pathname.split('/').slice(2).join('/')
  return rest ? `/${rest}` : '/'
}

export interface SeoBreadcrumb {
  name: string
  /** App-internal, language-prefixed path, e.g. withLang(lang, '/raboty'). */
  path: string
}

export interface SeoProps {
  /** Page-specific title; the brand name is appended automatically. */
  title: string
  description: string
  /** Absolute or root-relative image URL for OG/Twitter cards. */
  image?: string
  noindex?: boolean
  breadcrumbs?: SeoBreadcrumb[]
}

export function Seo({ title, description, image, noindex, breadcrumbs }: SeoProps) {
  const { t } = useTranslation()
  const lang = useLang()
  const { pathname } = useLocation()
  const rest = pathWithoutLang(pathname)
  const langPath = (code: string) => `${SITE_URL}/${code}${rest === '/' ? '' : rest}`
  const canonical = langPath(lang)
  const fullTitle = `${title} — ${t('seo.brandName')}`
  const ogImage = image
    ? (image.startsWith('http') ? image : `${SITE_URL}${image}`)
    : `${SITE_URL}/logo.png`

  const breadcrumbJsonLd = breadcrumbs?.length
    ? {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: breadcrumbs.map((b, i) => ({
          '@type': 'ListItem',
          position: i + 1,
          name: b.name,
          item: `${SITE_URL}${b.path}`,
        })),
      }
    : null

  return (
    <Helmet>
      <html lang={lang} />
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      {noindex && <meta name="robots" content="noindex, nofollow" />}

      <link rel="canonical" href={canonical} />
      {LANGUAGES.map(l => (
        <link key={l.code} rel="alternate" hrefLang={l.code} href={langPath(l.code)} />
      ))}
      <link rel="alternate" hrefLang="x-default" href={langPath(DEFAULT_LANG)} />

      <meta property="og:type" content="website" />
      <meta property="og:site_name" content={t('seo.brandName')} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={canonical} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:locale" content={OG_LOCALE[lang] ?? 'ru_RU'} />

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImage} />

      {breadcrumbJsonLd && (
        <script type="application/ld+json">{JSON.stringify(breadcrumbJsonLd)}</script>
      )}
    </Helmet>
  )
}
